import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import classnames from 'classnames';
import { toast } from 'react-toastify';
import CrudeInfoTab from './CrudeInfoTab';
import EvaluationTab from './EvaluationTab';
import ImplementationTab from './ImplementationTab';
import mapDetailToForm from './mapDetailToForm';
import { downloadCrudeDocument } from './registrationApi';

// Read-only expanded view of one assay row on the Crude Registration Detail page
// (rule-crude-registration.md §Detail — chevron expand). Renders the same three
// wizard tabs (Assay & Yield / Evaluation / Implementation) as the Create/Edit
// flow but with `readOnly`, so the layout matches exactly while every field is
// non-editable and the Next/Prev/Submit + document Add/Delete affordances are
// hidden. Seeded from the GetCrude `details[]` row via the shared mapDetailToForm.
//
// NOTE: documents are not hydrated yet (no download endpoint wired — see the
// deferred §7 note in AssayEditorSection.js), so the read-only document tables
// render empty for now; the assay/yield/implementation fields are fully shown.
const TAB_CONFIG = [
  { id: 1, icon: 'ri-flask-line', label: 'ASSAY & YIELD' },
  { id: 2, icon: 'ri-file-list-3-line', label: 'EVALUATION' },
  { id: 3, icon: 'ri-settings-3-line', label: 'IMPLEMENTATION' },
];

const noop = () => {};

// Group an assay row's saved documents by evaluationId for the read-only tabs.
// Each carries `existingId` (CrudeDocument id) so the Download action can fetch it.
const seedExistingDocs = detail => {
  const grouped = {};
  (detail?.documents || []).forEach(doc => {
    const key = doc.evaluationId;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push({
      existingId: doc.id,
      fileName: doc.fileName,
      refDocument: doc.refDocument,
      effDate: doc.effDate,
    });
  });
  return grouped;
};

const AssayDetailTabs = ({ crude, detail }) => {
  const [activeTab, setActiveTab] = useState(1);
  const form = useMemo(() => mapDetailToForm(detail, crude), [detail, crude]);
  const documentsByEval = useMemo(() => seedExistingDocs(detail), [detail]);

  // §7 — download a saved document as a file (PDF). Same blob→anchor pattern as
  // AuditView's export; the crude client returns the Blob directly.
  const handleDownload = async row => {
    if (!row?.existingId) return;
    try {
      const blob = await downloadCrudeDocument(row.existingId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = row.fileName || `document-${row.existingId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(`Failed to download document: ${error}`);
    }
  };

  return (
    <div className='p-2'>
      <Nav tabs className='nav-tabs-custom nav-primary mb-3'>
        {TAB_CONFIG.map(tab => (
          <NavItem key={tab.id}>
            <NavLink
              className={classnames({ active: activeTab === tab.id })}
              style={{ cursor: 'pointer' }}
              onClick={() => setActiveTab(tab.id)}
            >
              <i className={`${tab.icon} align-middle me-1`}></i>
              <span className='fw-semibold'>{tab.label}</span>
            </NavLink>
          </NavItem>
        ))}
      </Nav>

      <TabContent activeTab={activeTab}>
        <TabPane tabId={1}>
          {activeTab === 1 && (
            <CrudeInfoTab
              form={form}
              setForm={noop}
              errors={{}}
              onNext={noop}
              readOnly
              hideGeneral
            />
          )}
        </TabPane>
        <TabPane tabId={2}>
          {activeTab === 2 && (
            <EvaluationTab
              form={form}
              setForm={noop}
              documentsByEval={documentsByEval}
              onAddDocument={noop}
              onDeleteDocument={noop}
              onNext={noop}
              onBack={noop}
              readOnly
              onDownload={handleDownload}
            />
          )}
        </TabPane>
        <TabPane tabId={3}>
          {activeTab === 3 && (
            <ImplementationTab
              form={form}
              setForm={noop}
              documentsByEval={documentsByEval}
              onAddDocument={noop}
              onDeleteDocument={noop}
              onSubmit={noop}
              onBack={noop}
              readOnly
              onDownload={handleDownload}
            />
          )}
        </TabPane>
      </TabContent>
    </div>
  );
};

AssayDetailTabs.propTypes = {
  crude: PropTypes.object.isRequired,
  detail: PropTypes.object.isRequired,
};

export default AssayDetailTabs;
